// ===========================================
// 🔧 SERVICIO COMPLETO ACTUALIZADO: src/components/AGTFin/services/agtFinService.js
// ===========================================

import axios from 'axios';
import { getAuth } from 'firebase/auth';
import { 
  formatCurrency, 
  formatNumber,
  calculateMonthlySavings,
  getTimeframesByGoal,
  getGoalEstimatedCosts,
  validateCustomDate,
  getTimeRecommendations,
  getTimeframeDescription,
  timeframeOptions
} from '../utils/financialUtils';

const MONTHS_ES = [
  'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
  'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
];

// Configuración base para las peticiones al API según el entorno
const API_BASE = process.env.NODE_ENV === 'development'
  ? 'http://localhost:5005'
  : (process.env.REACT_APP_API_FIN_URL);

// Prefijo de API según entorno
const API_PREFIX = process.env.NODE_ENV === 'development' ? '/api' : '';

// URL completa
const API_URL = `${API_BASE}${API_PREFIX}`;

console.log('🔧 AGTFinService - API URL configurada:', API_URL);

class AGTFinService {
  constructor() {
    // Crear cliente axios como propiedad de la instancia
    this.apiClient = axios.create({
      baseURL: API_URL,
      headers: {
        'Content-Type': 'application/json'
      },
      withCredentials: true,
      timeout: 30000 // 30 segundos
    });

    // Configurar interceptors
    this.setupInterceptors();
  }

  /**
   * Configura los interceptors para el cliente axios
   */
  setupInterceptors() {
    // Interceptor para añadir token de autenticación
    this.apiClient.interceptors.request.use(
      async (config) => {
        try {
          const auth = getAuth();
          const user = auth.currentUser;
         
          if (user) {
            const token = await user.getIdToken();
            config.headers['Authorization'] = `Bearer ${token}`;
            console.log('🔑 Token agregado a la petición:', config.url);
          } else {
            console.warn('⚠️ No hay usuario autenticado para:', config.url);
          }
        } catch (error) {
          console.error('❌ Error al obtener token de Firebase:', error);
        }
        return config;
      },
      (error) => {
        console.error('❌ Error en interceptor de request:', error);
        return Promise.reject(error);
      }
    );

    // Interceptor para manejo de respuestas y errores
    this.apiClient.interceptors.response.use(
      (response) => {
        console.log('✅ Respuesta exitosa:', response.config.url, response.status);
        return response;
      },
      (error) => {
        console.error('❌ Error en respuesta:', {
          url: error.config?.url,
          status: error.response?.status,
          message: error.message,
          data: error.response?.data
        });
        
        if (error.response?.status === 401) {
          console.warn('🔓 Token de autenticación inválido o expirado');
        }
        
        // Mejorar el mensaje de error para debugging
        if (error.code === 'ECONNREFUSED') {
          console.error('🔌 Error de conexión: El servidor no está disponible');
          error.userMessage = 'No se puede conectar al servidor. Verifica tu conexión.';
        } else if (error.response?.status >= 500) {
          error.userMessage = 'Error interno del servidor. Intenta nuevamente.';
        } else if (error.response?.status === 404) {
          error.userMessage = 'Recurso no encontrado.';
        } else if (error.response?.status === 400) {
          error.userMessage = error.response.data?.error || 'Datos inválidos.';
        }
        
        return Promise.reject(error);
      }
    );
  }

  /**
   * ========================================
   * MÉTODOS DE PERFIL FINANCIERO
   * ========================================
   */

  /**
   * Verifica si el usuario tiene un perfil financiero creado
   * @param {string} userId - ID del usuario (no usado en URL, solo para compatibilidad)
   * @returns {Promise<boolean>} - true si tiene perfil, false si no
   */
  async checkUserProfile(userId) {
    try {
      if (!userId) {
        throw new Error('userId es requerido');
      }
      
      console.log('🔍 Verificando perfil para usuario:', userId);
      const response = await this.apiClient.get(`/agtfin/profile/check`);
      return response.data.hasProfile || false;
    } catch (error) {
      console.error('❌ Error verificando perfil del usuario:', error);
      if (error.response?.status === 404) {
        return false;
      }
      throw error;
    }
  }

  /**
   * Obtiene el perfil financiero del usuario
   * @param {string} userId - ID del usuario (no usado en URL, solo para compatibilidad)
   * @returns {Promise<Object>} - Datos del perfil financiero
   */
  async getUserProfile(userId) {
    try {
      if (!userId) {
        throw new Error('userId es requerido');
      }
      
      console.log('📄 Obteniendo perfil para usuario:', userId);
      const response = await this.apiClient.get(`/agtfin/profile`);
      return response.data.profile || response.data;
    } catch (error) {
      console.error('❌ Error obteniendo perfil del usuario:', error);
      throw error;
    }
  }

  /**
   * Crea o actualiza el perfil financiero del usuario
   * @param {string} userId - ID del usuario (no usado en URL, solo para compatibilidad)
   * @param {Object} profileData - Datos del perfil financiero
   * @returns {Promise<Object>} - Perfil creado/actualizado
   */
  async saveUserProfile(userId, profileData) {
    try {
      if (!userId) {
        throw new Error('userId es requerido');
      }
      
      console.log('💾 Guardando perfil para usuario:', userId);
      const response = await this.apiClient.post(`/agtfin/profile`, {
        ...profileData,
        updatedAt: new Date().toISOString()
      });
      return response.data;
    } catch (error) {
      console.error('❌ Error guardando perfil del usuario:', error);
      throw error;
    }
  }

  /**
   * Actualiza campos específicos del perfil financiero
   * @param {string} userId - ID del usuario (no usado en URL, solo para compatibilidad)
   * @param {Object} updateData - Campos a actualizar
   * @returns {Promise<Object>} - Perfil actualizado
   */
  async updateUserProfile(userId, updateData) {
    try {
      if (!userId) {
        throw new Error('userId es requerido');
      }
      
      console.log('🔄 Actualizando perfil para usuario:', userId);
      const response = await this.apiClient.patch(`/agtfin/profile`, {
        ...updateData,
        updatedAt: new Date().toISOString()
      });
      return response.data;
    } catch (error) {
      console.error('❌ Error actualizando perfil del usuario:', error);
      throw error;
    }
  }

  /**
   * Elimina el perfil financiero del usuario
   * @param {string} userId - ID del usuario (no usado en URL, solo para compatibilidad)
   * @returns {Promise<boolean>} - true si se eliminó correctamente
   */
  async deleteUserProfile(userId) {
    try {
      if (!userId) {
        throw new Error('userId es requerido');
      }
      
      console.log('🗑️ Eliminando perfil para usuario:', userId);
      await this.apiClient.delete(`/agtfin/profile`);
      return true;
    } catch (error) {
      console.error('❌ Error eliminando perfil del usuario:', error);
      throw error;
    }
  }

  /**
   * Obtiene recomendaciones personalizadas basadas en el perfil del usuario
   * @returns {Promise<Object>} - Recomendaciones personalizadas
   */
  async getUserRecommendations() {
    try {
      console.log('💡 Obteniendo recomendaciones del usuario');
      const response = await this.apiClient.get('/agtfin/profile/recommendations');
      return response.data;
    } catch (error) {
      console.error('❌ Error obteniendo recomendaciones:', error);
      throw error;
    }
  }

  /**
   * Obtiene el resumen financiero del usuario
   * @returns {Promise<Object>} - Resumen financiero
   */
  async getFinancialSummary() {
    try {
      console.log('📊 Obteniendo resumen financiero');
      const response = await this.apiClient.get('/agtfin/summary');
      return response.data;
    } catch (error) {
      console.error('❌ Error obteniendo resumen financiero:', error);
      throw error;
    }
  }

  /**
   * ========================================
   * MÉTODOS DE INGRESOS
   * ========================================
   */

  /**
   * Crear nuevo ingreso
   * @param {Object} incomeData - Datos del ingreso a crear
   * @returns {Promise<Object>} - Ingreso creado
   */
  async createIncome(incomeData) {
    try {
      console.log('💰 Creando nuevo ingreso:', incomeData);
      const response = await this.apiClient.post('/agtfin/income', incomeData);
      console.log('✅ Ingreso creado exitosamente:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ Error creando ingreso:', error);
      throw error;
    }
  }

  /**
   * Obtener todos los ingresos del usuario
   * @param {Object} options - Opciones de filtrado
   * @returns {Promise<Object>} - Lista de ingresos
   */
  async getIncomes(options = {}) {
    try {
      console.log('📋 Obteniendo ingresos con opciones:', options);
      const params = new URLSearchParams();
      
      if (options.incomeType) params.append('incomeType', options.incomeType);
      if (options.startDate) params.append('startDate', options.startDate);
      if (options.endDate) params.append('endDate', options.endDate);
      if (options.limit) params.append('limit', options.limit.toString());
      
      const queryString = params.toString();
      const url = queryString ? `/agtfin/income?${queryString}` : '/agtfin/income';
      
      const response = await this.apiClient.get(url);
      console.log('✅ Ingresos obtenidos:', response.data.incomes?.length || 0);
      return response.data;
    } catch (error) {
      console.error('❌ Error obteniendo ingresos:', error);
      throw error;
    }
  }

  /**
   * Obtener ingreso por ID
   * @param {string} incomeId - ID del ingreso
   * @returns {Promise<Object>} - Datos del ingreso
   */
  async getIncomeById(incomeId) {
    try {
      console.log('🔍 Obteniendo ingreso por ID:', incomeId);
      const response = await this.apiClient.get(`/agtfin/income/${incomeId}`);
      return response.data;
    } catch (error) {
      console.error('❌ Error obteniendo ingreso por ID:', error);
      throw error;
    }
  }

  /**
   * Actualizar ingreso
   * @param {string} incomeId - ID del ingreso
   * @param {Object} updateData - Datos a actualizar
   * @returns {Promise<Object>} - Ingreso actualizado
   */
  async updateIncome(incomeId, updateData) {
    try {
      console.log('🔄 Actualizando ingreso:', incomeId, updateData);
      const response = await this.apiClient.put(`/agtfin/income/${incomeId}`, updateData);
      console.log('✅ Ingreso actualizado exitosamente');
      return response.data;
    } catch (error) {
      console.error('❌ Error actualizando ingreso:', error);
      throw error;
    }
  }

  /**
   * Eliminar ingreso
   * @param {string} incomeId - ID del ingreso
   * @returns {Promise<Object>} - Confirmación de eliminación
   */
  async deleteIncome(incomeId) {
    try {
      console.log('🗑️ Eliminando ingreso:', incomeId);
      const response = await this.apiClient.delete(`/agtfin/income/${incomeId}`);
      console.log('✅ Ingreso eliminado exitosamente');
      return response.data;
    } catch (error) {
      console.error('❌ Error eliminando ingreso:', error);
      throw error;
    }
  }

  /**
   * Obtener resumen de ingresos por tipo
   * @param {string} startDate - Fecha de inicio (opcional)
   * @param {string} endDate - Fecha de fin (opcional)
   * @returns {Promise<Object>} - Resumen de ingresos
   */
  async getIncomeSummary(startDate, endDate) {
    try {
      console.log('📊 Obteniendo resumen de ingresos');
      const params = new URLSearchParams();
      if (startDate) params.append('startDate', startDate);
      if (endDate) params.append('endDate', endDate);
      
      const queryString = params.toString();
      const url = queryString ? `/agtfin/income/summary?${queryString}` : '/agtfin/income/summary';
      
      const response = await this.apiClient.get(url);
      return response.data;
    } catch (error) {
      console.error('❌ Error obteniendo resumen de ingresos:', error);
      throw error;
    }
  }

  /**
   * Obtener ingresos del mes actual
   * @returns {Promise<Object>} - Ingresos del mes actual con resumen
   */
  async getCurrentMonthIncomes() {
    try {
      console.log('📅 Obteniendo ingresos del mes actual');
      const response = await this.apiClient.get('/agtfin/income/current-month');
      return response.data;
    } catch (error) {
      console.error('❌ Error obteniendo ingresos del mes actual:', error);
      throw error;
    }
  }

  /**
   * Obtener estadísticas de ingresos
   * @param {string} period - Período de estadísticas (1month, 3months, 6months, 1year)
   * @returns {Promise<Object>} - Estadísticas de ingresos
   */
  async getIncomeStats(period = '3months') {
    try {
      console.log('📈 Obteniendo estadísticas de ingresos para período:', period);
      const response = await this.apiClient.get(`/agtfin/income/stats?period=${period}`);
      return response.data;
    } catch (error) {
      console.error('❌ Error obteniendo estadísticas de ingresos:', error);
      throw error;
    }
  }

  /**
   * ========================================
   * MÉTODOS DE UTILIDADES FINANCIERAS
   * ========================================
   */

  /**
   * Calcula el ahorro mensual necesario para una meta (versión actualizada)
   * @param {number} goalAmount - Monto de la meta
   * @param {string} timeframe - Plazo para la meta
   * @param {string} customDate - Fecha personalizada (opcional)
   * @returns {number} - Ahorro mensual necesario
   */
  calculateMonthlySavings(goalAmount, timeframe, customDate = null) {
    return calculateMonthlySavings(goalAmount, timeframe, customDate);
  }

  /**
   * Obtiene información formateada sobre los costos estimados de una meta
   * @param {string} goalId - ID de la meta
   * @param {string} currency - Moneda
   * @returns {Object} - Costos estimados formateados
   */
  getGoalEstimatedCosts(goalId, currency = 'USD') {
    return getGoalEstimatedCosts(goalId, currency);
  }

  /**
   * Obtiene las opciones de tiempo disponibles para una meta específica
   * @param {string} goalId - ID de la meta
   * @returns {Array} - Array de opciones de tiempo
   */
  getTimeframesByGoal(goalId) {
    return getTimeframesByGoal(goalId);
  }

  /**
   * Obtiene recomendaciones de tiempo basadas en el monto
   * @param {number} goalAmount - Monto de la meta
   * @param {string} currency - Moneda
   * @returns {Object} - Recomendaciones de tiempo
   */
  getTimeRecommendations(goalAmount, currency = 'USD') {
    return getTimeRecommendations(goalAmount, currency);
  }

  /**
   * Valida una fecha personalizada
   * @param {string} dateString - Fecha en formato string
   * @returns {Object} - Resultado de validación
   */
  validateCustomDate(dateString) {
    return validateCustomDate(dateString);
  }

  /**
   * Obtiene descripción legible de un timeframe
   * @param {string} timeframe - Timeframe ID
   * @param {string} customDate - Fecha personalizada (opcional)
   * @returns {string} - Descripción del timeframe
   */
  getTimeframeDescription(timeframe, customDate = null) {
    return getTimeframeDescription(timeframe, customDate);
  }

  /**
   * Formatea un monto como moneda
   * @param {number} amount - Monto a formatear
   * @param {string} currency - Código de moneda
   * @returns {string} - Monto formateado como moneda
   */
  formatCurrency(amount, currency = 'USD') {
    return formatCurrency(amount, currency);
  }

  /**
   * Formatea un número con separadores de miles
   * @param {number} amount - Número a formatear
   * @param {string} currency - Código de moneda para el locale
   * @returns {string} - Número formateado
   */
  formatNumber(amount, currency = 'USD') {
    return formatNumber(amount, currency);
  }

  /**
   * ========================================
   * MÉTODOS DE ANÁLISIS Y RECOMENDACIONES
   * ========================================
   */

  /**
   * Obtiene información sobre un propósito financiero específico
   * @param {string} purposeId - ID del propósito financiero
   * @returns {Object} - Información del propósito
   */
  getFinancialPurposeInfo(purposeId) {
    const purposes = {
      expense_control: {
        id: 'expense_control',
        title: 'Controlar mis Gastos',
        description: 'Ideal para personas que quieren entender mejor sus patrones de gasto',
        benefits: [
          'Identificar gastos innecesarios',
          'Establecer límites por categoría',
          'Generar reportes de gastos',
          'Recibir alertas de presupuesto'
        ],
        nextSteps: [
          'Configurar categorías de gastos',
          'Establecer presupuesto mensual',
          'Conectar cuentas bancarias'
        ],
        recommendedFeatures: [
          'Seguimiento de gastos en tiempo real',
          'Categorización automática',
          'Alertas de presupuesto',
          'Reportes mensuales'
        ],
        tips: [
          'Revisa tus gastos semanalmente',
          'Establece un presupuesto realista para cada categoría',
          'Usa la regla 50/30/20 como punto de partida',
          'Identifica gastos hormigas que suman mucho al mes'
        ]
      },
      save_money: {
        id: 'save_money',
        title: 'Ahorrar Dinero',
        description: 'Perfecto para quienes quieren crear el hábito del ahorro',
        benefits: [
          'Automatizar transferencias de ahorro',
          'Establecer metas de ahorro',
          'Seguimiento de progreso',
          'Tips personalizados de ahorro'
        ],
        nextSteps: [
          'Definir meta de ahorro',
          'Configurar ahorro automático',
          'Elegir cuenta de ahorro'
        ],
        recommendedFeatures: [
          'Ahorro automático',
          'Metas de ahorro específicas',
          'Seguimiento de progreso',
          'Calculadora de interés compuesto'
        ],
        tips: [
          'Automatiza tu ahorro para que sea lo primero',
          'Comienza con pequeñas cantidades y aumenta gradualmente',
          'Ten una meta específica para mantenerte motivado',
          'Aprovecha las cuentas de ahorro con mejores rendimientos'
        ]
      },
      invest_grow: {
        id: 'invest_grow',
        title: 'Invertir y Hacer Crecer mi Dinero',
        description: 'Para quienes buscan hacer que su dinero genere rendimientos',
        benefits: [
          'Calculadoras de inversión',
          'Seguimiento de portafolio',
          'Análisis de riesgo',
          'Educación financiera'
        ],
        nextSteps: [
          'Evaluar perfil de riesgo',
          'Aprender sobre inversiones',
          'Diversificar portafolio'
        ],
        recommendedFeatures: [
          'Análisis de perfil de riesgo',
          'Seguimiento de inversiones',
          'Calculadora de ROI',
          'Recursos educativos'
        ],
        tips: [
          'Diversifica tu portafolio para reducir riesgos',
          'Invierte solo lo que puedes permitirte perder',
          'Edúcate continuamente sobre inversiones',
          'Mantén una perspectiva a largo plazo'
        ]
      },
      income_distribution: {
        id: 'income_distribution',
        title: 'Distribuir mis Ingresos Inteligentemente',
        description: 'Ideal para optimizar la asignación de ingresos',
        benefits: [
          'Aplicar reglas de distribución',
          'Balancear gastos y ahorro',
          'Optimizar flujo de efectivo',
          'Planificación financiera'
        ],
        nextSteps: [
          'Definir porcentajes ideales',
          'Configurar distribución automática',
          'Monitorear cumplimiento'
        ],
        recommendedFeatures: [
          'Reglas de distribución automática',
          'Seguimiento de porcentajes',
          'Alertas de desviación',
          'Planificador financiero'
        ],
        tips: [
          'Usa la regla 50/30/20 como base',
          'Ajusta los porcentajes según tus prioridades',
          'Revisa y ajusta tu distribución mensualmente',
          'Prioriza el pago de deudas de alto interés'
        ]
      }
    };

    return purposes[purposeId] || null;
  }

  /**
   * Obtiene información detallada sobre una meta específica
   * @param {string} goalId - ID de la meta
   * @param {string} currency - Moneda para mostrar costos
   * @returns {Object} - Información completa de la meta
   */
  getGoalInfo(goalId, currency = 'USD') {
    const goals = {
      buy_house: {
        id: 'buy_house',
        title: 'Comprar una Casa',
        category: 'Vivienda',
        description: 'Una de las inversiones más importantes de la vida',
        tips: [
          'Considera el pie (10-20% del valor)',
          'Incluye gastos de escritura y trámites (2-5% adicional)',
          'Evalúa tu capacidad de endeudamiento',
          'Investiga subsidios gubernamentales disponibles',
          'Considera la ubicación y potencial de valorización',
          'Incluye costos de mantenimiento en tu presupuesto'
        ],
        estimatedCosts: getGoalEstimatedCosts(goalId, currency),
        timeframes: getTimeframesByGoal(goalId),
        factors: [
          'Ubicación y zona',
          'Tipo de propiedad (casa, departamento)',
          'Estado de la construcción',
          'Servicios incluidos',
          'Potencial de valorización'
        ],
        considerations: [
          'Gastos adicionales: notaría, registro, tasación',
          'Seguro de hogar obligatorio',
          'Impuestos asociados a la propiedad',
          'Costos de mudanza y adaptación'
        ]
      },
      buy_car: {
        id: 'buy_car',
        title: 'Comprar un Auto',
        category: 'Transporte',
        description: 'Medio de transporte que puede ser necesidad o comodidad',
        tips: [
          'Define si será nuevo o usado',
          'Considera costos de mantenimiento anual',
          'Evalúa opciones de financiamiento vs pago contado',
          'Incluye seguro obligatorio y permisos',
          'Investiga el valor de reventa',
          'Considera combustible y estacionamiento'
        ],
        estimatedCosts: getGoalEstimatedCosts(goalId, currency),
        timeframes: getTimeframesByGoal(goalId),
        factors: [
          'Marca y modelo',
          'Año de fabricación',
          'Kilometraje (si es usado)',
          'Tipo de combustible',
          'Costo de repuestos y mantenimiento'
        ],
        considerations: [
          'Depreciación anual del vehículo',
          'Costos de mantenimiento preventivo',
          'Seguro todo riesgo vs terceros',
          'Revisión técnica y permisos anuales'
        ]
      },
      education: {
        id: 'education',
        title: 'Estudiar/Capacitarme',
        category: 'Educación',
        description: 'Inversión en conocimiento y desarrollo profesional',
        tips: [
          'Investiga becas y opciones de financiamiento',
          'Considera educación online vs presencial',
          'Evalúa el retorno de inversión esperado',
          'Planifica tiempo de estudio con trabajo',
          'Incluye materiales y herramientas necesarias',
          'Considera certificaciones complementarias'
        ],
        estimatedCosts: getGoalEstimatedCosts(goalId, currency),
        timeframes: getTimeframesByGoal(goalId),
        factors: [
          'Tipo de programa (técnico, universitario, posgrado)',
          'Modalidad (presencial, online, híbrida)',
          'Duración del programa',
          'Prestigio de la institución',
          'Materiales y recursos adicionales'
        ],
        considerations: [
          'Costo de oportunidad por tiempo dedicado',
          'Materiales y libros adicionales',
          'Transporte y alimentación (si es presencial)',
          'Certificaciones y exámenes adicionales'
        ]
      },
      travel: {
        id: 'travel',
        title: 'Viajar',
        category: 'Estilo de vida',
        description: 'Experiencias y descanso que enriquecen la vida',
        tips: [
          'Planifica con 6+ meses de anticipación para mejores precios',
          'Considera viajar en temporada baja',
          'Evalúa diferentes tipos de alojamiento',
          'Incluye seguro de viaje obligatoriamente',
          'Reserva un 20% extra para imprevistos',
          'Investiga actividades y costos locales'
        ],
        estimatedCosts: getGoalEstimatedCosts(goalId, currency),
        timeframes: getTimeframesByGoal(goalId),
        factors: [
          'Destino (nacional vs internacional)',
          'Duración del viaje',
          'Tipo de alojamiento',
          'Temporada del año',
          'Actividades planificadas'
        ],
        considerations: [
          'Visas y documentación necesaria',
          'Vacunas requeridas para el destino',
          'Cambio de moneda y comisiones',
          'Roaming internacional o SIM local'
        ]
      },
      emergency_fund: {
        id: 'emergency_fund',
        title: 'Fondo de Emergencia',
        category: 'Seguridad financiera',
        description: 'Colchón financiero para situaciones imprevistas',
        tips: [
          'Calcula 3-6 meses de gastos esenciales',
          'Mantén en cuenta de fácil acceso pero que genere rendimiento',
          'No lo uses para gastos no urgentes',
          'Revisa y ajusta el monto cada 6 meses',
          'Sepáralo completamente de tu cuenta corriente',
          'Automatiza las contribuciones mensuales'
        ],
        estimatedCosts: {
          calculation: 'monthly_expenses * 3_to_6',
          description: 'Multiplica tus gastos mensuales esenciales por 3-6'
        },
        timeframes: getTimeframesByGoal(goalId),
        factors: [
          'Estabilidad de ingresos',
          'Dependientes económicos',
          'Gastos médicos potenciales',
          'Estabilidad laboral del sector',
          'Otros seguros disponibles'
        ],
        considerations: [
          'Solo para verdaderas emergencias',
          'Cuenta separada con fácil acceso',
          'Rendimiento superior a inflación',
          'Revisión periódica del monto necesario'
        ]
      },
      business: {
        id: 'business',
        title: 'Emprender un Negocio',
        category: 'Emprendimiento',
        description: 'Crear una fuente de ingresos propia',
        tips: [
          'Elabora un plan de negocios detallado',
          'Calcula capital de trabajo inicial',
          'Considera gastos operativos de los primeros 6 meses',
          'Mantén reserva del 30% para imprevistos',
          'Investiga regulaciones y permisos necesarios',
          'Valida tu idea con clientes potenciales'
        ],
        estimatedCosts: getGoalEstimatedCosts(goalId, currency),
        timeframes: getTimeframesByGoal(goalId),
        factors: [
          'Tipo de negocio y sector',
          'Necesidad de local físico',
          'Inventario inicial requerido',
          'Equipos y tecnología necesaria',
          'Personal inicial'
        ],
        considerations: [
          'Permisos y licencias gubernamentales',
          'Seguros empresariales necesarios',
          'Capital de trabajo para primeros meses',
          'Marketing y promoción inicial'
        ]
      }
    };

    const goal = goals[goalId];
    if (!goal) return null;

    // Agregar recomendaciones de tiempo específicas
    if (goal.estimatedCosts && goal.estimatedCosts.minRaw) {
      const timeRec = getTimeRecommendations(goal.estimatedCosts.minRaw, currency);
      goal.timeRecommendations = timeRec;
    }

    return goal;
  }

  /**
   * Calcula la distribución ideal de ingresos según el propósito financiero
   * @param {number} monthlyIncome - Ingresos mensuales
   * @param {string} purposeId - ID del propósito financiero
   * @param {Object} options - Opciones adicionales
   * @returns {Object} - Distribución recomendada
   */
  calculateIncomeDistribution(monthlyIncome, purposeId, options = {}) {
    const { hasDebt = false, dependents = 0 } = options;
    
    let distribution = {};
    
    switch (purposeId) {
      case 'expense_control':
        distribution = {
          essentials: { percentage: 50, amount: monthlyIncome * 0.50 },
          lifestyle: { percentage: 30, amount: monthlyIncome * 0.30 },
          savings: { percentage: 20, amount: monthlyIncome * 0.20 }
        };
        break;
        
      case 'save_money':
        distribution = {
          essentials: { percentage: 45, amount: monthlyIncome * 0.45 },
          lifestyle: { percentage: 25, amount: monthlyIncome * 0.25 },
          savings: { percentage: 30, amount: monthlyIncome * 0.30 }
        };
        break;
        
      case 'invest_grow':
        distribution = {
          essentials: { percentage: 50, amount: monthlyIncome * 0.50 },
          lifestyle: { percentage: 20, amount: monthlyIncome * 0.20 },
          savings: { percentage: 15, amount: monthlyIncome * 0.15 },
          investments: { percentage: 15, amount: monthlyIncome * 0.15 }
        };
        break;
        
      case 'income_distribution':
        distribution = {
          essentials: { percentage: 50, amount: monthlyIncome * 0.50 },
          lifestyle: { percentage: 30, amount: monthlyIncome * 0.30 },
          savings: { percentage: 10, amount: monthlyIncome * 0.10 },
          investments: { percentage: 10, amount: monthlyIncome * 0.10 }
        };
        break;
        
      default:
        distribution = {
          essentials: { percentage: 50, amount: monthlyIncome * 0.50 },
          lifestyle: { percentage: 30, amount: monthlyIncome * 0.30 },
          savings: { percentage: 20, amount: monthlyIncome * 0.20 }
        };
    }
    
    // Ajustar por deudas
    if (hasDebt) {
      const debtPayment = monthlyIncome * 0.15;
      distribution.debtPayment = { percentage: 15, amount: debtPayment };
      
      // Reducir otros porcentajes proporcionalmente
      Object.keys(distribution).forEach(key => {
        if (key !== 'debtPayment') {
          distribution[key].percentage *= 0.85;
          distribution[key].amount *= 0.85;
        }
      });
    }
    
    // Ajustar por dependientes
    if (dependents > 0) {
      const extraEssentials = dependents * 0.05;
      distribution.essentials.percentage += (extraEssentials * 100);
      distribution.essentials.amount += (monthlyIncome * extraEssentials);
      
      // Reducir ahorros/inversiones
      if (distribution.savings) {
        distribution.savings.percentage -= (extraEssentials * 50);
        distribution.savings.amount -= (monthlyIncome * extraEssentials * 0.5);
      }
      if (distribution.investments) {
        distribution.investments.percentage -= (extraEssentials * 50);
        distribution.investments.amount -= (monthlyIncome * extraEssentials * 0.5);
      }
    }
    
    return {
      monthlyIncome,
      distribution,
      recommendations: this.getDistributionRecommendations(distribution, purposeId)
    };
  }

  /**
   * Obtiene recomendaciones específicas para la distribución de ingresos
   * @param {Object} distribution - Distribución calculada
   * @param {string} purposeId - ID del propósito financiero
   * @returns {Array} - Array de recomendaciones
   */
  getDistributionRecommendations(distribution, purposeId) {
    const recommendations = [];
    
    if (distribution.savings && distribution.savings.percentage < 10) {
      recommendations.push({
        type: 'warning',
        message: 'Tu porcentaje de ahorro está por debajo del 10% recomendado',
        action: 'Intenta reducir gastos de estilo de vida para aumentar el ahorro'
      });
    }
    
    if (distribution.essentials && distribution.essentials.percentage > 60) {
      recommendations.push({
        type: 'alert',
        message: 'Tus gastos esenciales representan más del 60% de tus ingresos',
        action: 'Busca maneras de reducir gastos fijos o aumentar ingresos'
      });
    }
    
    if (distribution.investments && distribution.investments.percentage > 0) {
      recommendations.push({
        type: 'success',
        message: 'Excelente que destines dinero a inversiones',
        action: 'Asegúrate de tener un fondo de emergencia antes de invertir'
      });
    }
    
    return recommendations;
  }

  /**
   * Obtiene estadísticas y métricas del perfil financiero
   * @param {Object} profileData - Datos del perfil
   * @returns {Object} - Estadísticas calculadas
   */
  getProfileStatistics(profileData) {
    const stats = {
      setupCompleteness: 0,
      goalProgress: 0,
      riskLevel: 'medium',
      timeToGoal: null,
      monthlyTarget: 0,
      completedSections: [],
      missingSections: []
    };
    
    // Calcular completeness del setup
    const requiredFields = ['fullName', 'age', 'occupation', 'currency', 'financialPurpose', 'primaryGoal'];
    const completedFields = requiredFields.filter(field => profileData[field]);
    stats.setupCompleteness = Math.round((completedFields.length / requiredFields.length) * 100);
    
    // Calcular progreso de meta
    if (profileData.goalAmount && profileData.goalTimeframe) {
      stats.monthlyTarget = this.calculateMonthlySavings(
        profileData.goalAmount,
        profileData.goalTimeframe,
        profileData.customGoalDate
      );
      
      if (profileData.currentSavings) {
        stats.goalProgress = Math.round((profileData.currentSavings / profileData.goalAmount) * 100);
      }
    }
    
    // Determinar nivel de riesgo basado en meta y tiempo
    if (profileData.goalAmount && profileData.goalTimeframe) {
      const monthsToGoal = this.calculateMonthsToGoal(profileData.goalTimeframe, profileData.customGoalDate);
      const monthlyRequired = profileData.goalAmount / monthsToGoal;
      
      if (monthlyRequired > 2000) {
        stats.riskLevel = 'high';
      } else if (monthlyRequired > 500) {
        stats.riskLevel = 'medium';
      } else {
        stats.riskLevel = 'low';
      }
      
      stats.timeToGoal = monthsToGoal;
    }
    
    return stats;
  }

  /**
   * Calcula meses hasta la meta
   * @param {string} timeframe - Timeframe seleccionado
   * @param {string} customDate - Fecha personalizada
   * @returns {number} - Número de meses
   */
  calculateMonthsToGoal(timeframe, customDate = null) {
    if (timeframe === 'custom' && customDate) {
      const today = new Date();
      const target = new Date(customDate);
      const yearDiff = target.getFullYear() - today.getFullYear();
      const monthDiff = target.getMonth() - today.getMonth();
      return (yearDiff * 12) + monthDiff;
    }
    
    const option = timeframeOptions.find(opt => opt.value === timeframe);
    return option ? option.months : 12;
  }

  /**
   * Obtiene consejos personalizados basados en el perfil
   * @param {Object} profileData - Datos del perfil
   * @returns {Array} - Array de consejos personalizados
   */
  getPersonalizedTips(profileData) {
    const tips = [];
    
    // Tips basados en edad
    if (profileData.age) {
      const age = parseInt(profileData.age);
      
      if (age < 25) {
        tips.push({
          category: 'age',
          title: 'Ventaja del tiempo',
          message: 'Tienes la mayor ventaja: el tiempo. Incluso pequeñas cantidades ahorradas ahora tendrán un gran impacto por el interés compuesto.',
          priority: 'high'
        });
      } else if (age >= 25 && age < 35) {
        tips.push({
          category: 'age',
          title: 'Momento ideal para invertir',
          message: 'Estás en la edad perfecta para tomar riesgos calculados. Considera diversificar entre ahorro e inversión.',
          priority: 'medium'
        });
      } else if (age >= 35 && age < 50) {
        tips.push({
          category: 'age',
          title: 'Planificación estratégica',
          message: 'Es momento de ser más estratégico con tus finanzas. Prioriza metas específicas y establece plazos claros.',
          priority: 'high'
        });
      } else if (age >= 50) {
        tips.push({
          category: 'age',
          title: 'Protección del patrimonio',
          message: 'Enfócate en preservar y hacer crecer de forma conservadora lo que has construido.',
          priority: 'high'
        });
      }
    }
    
    // Tips basados en propósito financiero
    if (profileData.financialPurpose) {
      const purposeInfo = this.getFinancialPurposeInfo(profileData.financialPurpose);
      if (purposeInfo && purposeInfo.tips) {
        purposeInfo.tips.forEach((tip, index) => {
          tips.push({
            category: 'purpose',
            title: `Consejo ${index + 1} para ${purposeInfo.title}`,
            message: tip,
            priority: index === 0 ? 'high' : 'medium'
          });
        });
      }
    }
    
    // Tips basados en meta principal
    if (profileData.primaryGoal) {
      const goalInfo = this.getGoalInfo(profileData.primaryGoal, profileData.currency);
      if (goalInfo && goalInfo.tips) {
        goalInfo.tips.slice(0, 3).forEach((tip, index) => {
          tips.push({
            category: 'goal',
            title: `Consejo para ${goalInfo.title}`,
            message: tip,
            priority: index === 0 ? 'high' : 'medium'
          });
        });
      }
    }
    
    // Tips basados en monto y timeframe
    if (profileData.goalAmount && profileData.goalTimeframe) {
      const monthlyTarget = this.calculateMonthlySavings(
        profileData.goalAmount, 
        profileData.goalTimeframe, 
        profileData.customGoalDate
      );
      
      const formattedMonthly = this.formatCurrency(monthlyTarget, profileData.currency);
      const formattedGoal = this.formatCurrency(profileData.goalAmount, profileData.currency);
      
      tips.push({
        category: 'calculation',
        title: 'Tu plan de ahorro',
        message: `Para alcanzar tu meta de ${formattedGoal}, necesitas ahorrar ${formattedMonthly} cada mes. Considera automatizar esta transferencia.`,
        priority: 'high'
      });
      
      // Tip adicional basado en el monto mensual
      if (monthlyTarget > 1000) {
        tips.push({
          category: 'strategy',
          title: 'Meta ambiciosa',
          message: 'Tu meta requiere un ahorro significativo. Considera dividirla en submetas más pequeñas o extender el plazo.',
          priority: 'medium'
        });
      } else if (monthlyTarget < 100) {
        tips.push({
          category: 'strategy',
          title: 'Oportunidad de crecimiento',
          message: 'Tu meta es muy alcanzable. Considera aumentar el monto o acortar el plazo para un mayor desafío.',
          priority: 'low'
        });
      }
    }
    
    // Tips basados en moneda
    if (profileData.currency && profileData.currency !== 'USD') {
      tips.push({
        category: 'currency',
        title: 'Considera la inflación',
        message: 'Asegúrate de que tu plan de ahorro considere la inflación de tu país para mantener el poder adquisitivo.',
        priority: 'medium'
      });
    }
    
    return tips.sort((a, b) => {
      const priorityOrder = { 'high': 3, 'medium': 2, 'low': 1 };
      return priorityOrder[b.priority] - priorityOrder[a.priority];
    });
  }

  /**
   * Valida los datos del perfil financiero
   * @param {Object} profileData - Datos del perfil a validar
   * @returns {Object} - Resultado de validación con errores si los hay
   */
  validateProfileData(profileData) {
    const errors = [];
    const warnings = [];
    
    // Validaciones obligatorias
    if (!profileData.fullName || profileData.fullName.trim().length < 2) {
      errors.push('El nombre completo es obligatorio y debe tener al menos 2 caracteres');
    }
    
    if (!profileData.age || profileData.age < 16 || profileData.age > 100) {
      errors.push('La edad debe estar entre 16 y 100 años');
    }
    
    if (!profileData.occupation || profileData.occupation.trim().length < 2) {
      errors.push('La ocupación es obligatoria');
    }
    
    if (!profileData.currency || !['USD', 'EUR', 'CLP', 'MXN', 'ARS', 'COP'].includes(profileData.currency)) {
      errors.push('Debe seleccionar una moneda válida');
    }
    
    if (!profileData.financialPurpose) {
      errors.push('Debe seleccionar un propósito financiero');
    }
    
    if (!profileData.primaryGoal) {
      errors.push('Debe seleccionar una meta principal');
    }
    
    // Validaciones de meta
    if (profileData.goalAmount) {
      const amount = parseFloat(profileData.goalAmount);
      if (isNaN(amount) || amount <= 0) {
        errors.push('El monto de la meta debe ser un número positivo');
      } else if (amount < 100) {
        warnings.push('El monto de la meta parece muy bajo, considera aumentarlo');
      }
    }
    
    if (profileData.goalTimeframe) {
      if (profileData.goalTimeframe === 'custom' && !profileData.customGoalDate) {
        errors.push('Debe especificar una fecha cuando selecciona tiempo personalizado');
      } else if (profileData.goalTimeframe === 'custom' && profileData.customGoalDate) {
        const validation = this.validateCustomDate(profileData.customGoalDate);
        if (!validation.valid) {
          errors.push(`Fecha personalizada inválida: ${validation.error}`);
        }
      }
    }
    
    // Validaciones de coherencia
    if (profileData.goalAmount && profileData.goalTimeframe) {
      const monthlyTarget = this.calculateMonthlySavings(
        profileData.goalAmount,
        profileData.goalTimeframe,
        profileData.customGoalDate
      );
      
      if (monthlyTarget > 5000) {
        warnings.push('El ahorro mensual requerido es muy alto, considera ajustar la meta o el tiempo');
      }
    }
    
    return {
      isValid: errors.length === 0,
      errors,
      warnings
    };
  }

  /**
   * Genera un reporte completo del perfil financiero
   * @param {Object} profileData - Datos del perfil
   * @returns {Object} - Reporte completo
   */
  generateProfileReport(profileData) {
    const validation = this.validateProfileData(profileData);
    const statistics = this.getProfileStatistics(profileData);
    const tips = this.getPersonalizedTips(profileData);
    const goalInfo = profileData.primaryGoal ? this.getGoalInfo(profileData.primaryGoal, profileData.currency) : null;
    const purposeInfo = profileData.financialPurpose ? this.getFinancialPurposeInfo(profileData.financialPurpose) : null;
    
    let incomeDistribution = null;
    if (profileData.monthlyIncome) {
      incomeDistribution = this.calculateIncomeDistribution(
        profileData.monthlyIncome,
        profileData.financialPurpose,
        {
          hasDebt: profileData.hasDebt || false,
          dependents: profileData.dependents || 0
        }
      );
    }
    
    return {
      profile: profileData,
      validation,
      statistics,
      tips: tips.slice(0, 10), // Limitar a 10 tips más importantes
      goalInfo,
      purposeInfo,
      incomeDistribution,
      recommendations: {
        immediate: tips.filter(tip => tip.priority === 'high').slice(0, 3),
        shortTerm: tips.filter(tip => tip.priority === 'medium').slice(0, 3),
        longTerm: tips.filter(tip => tip.priority === 'low').slice(0, 2)
      },
      createdAt: new Date().toISOString()
    };
  }

  /**
   * Obtiene plantillas de metas predefinidas según el perfil
   * @param {Object} profileData - Datos del perfil
   * @returns {Array} - Array de plantillas sugeridas
   */
  getGoalTemplates(profileData) {
    const templates = [];
    
    if (profileData.age && profileData.currency) {
      const age = parseInt(profileData.age);
      
      // Plantillas por edad
      if (age < 30) {
        templates.push({
          id: 'young_emergency',
          title: 'Fondo de Emergencia Joven',
          description: 'Fondo básico para empezar',
          goalAmount: 5000,
          timeframe: '1_year',
          category: 'emergency_fund'
        });
        
        templates.push({
          id: 'first_investment',
          title: 'Primera Inversión',
          description: 'Cantidad inicial para comenzar a invertir',
          goalAmount: 2000,
          timeframe: '6_months',
          category: 'invest_grow'
        });
      } else if (age >= 30 && age < 50) {
        templates.push({
          id: 'family_emergency',
          title: 'Fondo de Emergencia Familiar',
          description: 'Fondo robusto para gastos familiares',
          goalAmount: 15000,
          timeframe: '2_years',
          category: 'emergency_fund'
        });
        
        templates.push({
          id: 'house_downpayment',
          title: 'Pie para Casa',
          description: 'Ahorro para el pie de una vivienda',
          goalAmount: 50000,
          timeframe: '5_years',
          category: 'buy_house'
        });
      }
    }
    
    // Convertir montos según la moneda
    if (profileData.currency !== 'USD') {
      const exchangeRates = {
        'EUR': 0.85,
        'CLP': 900,
        'MXN': 18,
        'ARS': 800,
        'COP': 4000
      };
      
      const rate = exchangeRates[profileData.currency] || 1;
      templates.forEach(template => {
        template.goalAmount = Math.round(template.goalAmount * rate);
      });
    }
    
    return templates;
  }

  /**
   * Calcula proyecciones financieras basadas en el perfil
   * @param {Object} profileData - Datos del perfil
   * @param {number} years - Años a proyectar (por defecto 5)
   * @returns {Object} - Proyecciones calculadas
   */
  calculateProjections(profileData, years = 5) {
    const projections = {
      years,
      timeline: [],
      totalSaved: 0,
      totalGrowth: 0,
      finalAmount: 0
    };
    
    if (!profileData.goalAmount || !profileData.goalTimeframe) {
      return projections;
    }
    
    const monthlyTarget = this.calculateMonthlySavings(
      profileData.goalAmount,
      profileData.goalTimeframe,
      profileData.customGoalDate
    );
    
    const annualSavings = monthlyTarget * 12;
    const assumedReturn = 0.05; // 5% anual asumido
    
    let cumulativeSavings = 0;
    let cumulativeGrowth = 0;
    
    for (let year = 1; year <= years; year++) {
      cumulativeSavings += annualSavings;
      const growthThisYear = (cumulativeSavings + cumulativeGrowth) * assumedReturn;
      cumulativeGrowth += growthThisYear;
      
      const totalAmount = cumulativeSavings + cumulativeGrowth;
      
      projections.timeline.push({
        year,
        annualSavings,
        cumulativeSavings,
        growthThisYear,
        cumulativeGrowth,
        totalAmount,
        formattedTotalAmount: this.formatCurrency(totalAmount, profileData.currency)
      });
    }
    
    projections.totalSaved = cumulativeSavings;
    projections.totalGrowth = cumulativeGrowth;
    projections.finalAmount = cumulativeSavings + cumulativeGrowth;
    
    return projections;
  }

  /**
   * Obtiene métricas de progreso del usuario
   * @param {Object} profileData - Datos del perfil
   * @param {Object} currentData - Datos actuales del usuario
   * @returns {Object} - Métricas de progreso
   */
  getProgressMetrics(profileData, currentData = {}) {
    const metrics = {
      goalProgress: 0,
      timeProgress: 0,
      savingsRate: 0,
      onTrack: false,
      projectedCompletion: null,
      adjustmentNeeded: false
    };
    
    if (!profileData.goalAmount || !currentData.currentSavings) {
      return metrics;
    }
    
    // Progreso de la meta
    metrics.goalProgress = Math.min(100, (currentData.currentSavings / profileData.goalAmount) * 100);
    
    // Progreso de tiempo
    const totalMonths = this.calculateMonthsToGoal(profileData.goalTimeframe, profileData.customGoalDate);
    const monthsPassed = currentData.monthsPassed || 0;
    metrics.timeProgress = Math.min(100, (monthsPassed / totalMonths) * 100);
    
    // Tasa de ahorro
    if (currentData.monthlyIncome) {
      const monthlyTarget = this.calculateMonthlySavings(
        profileData.goalAmount,
        profileData.goalTimeframe,
        profileData.customGoalDate
      );
      metrics.savingsRate = (monthlyTarget / currentData.monthlyIncome) * 100;
    }
    
    // Determinar si está en camino
    metrics.onTrack = metrics.goalProgress >= (metrics.timeProgress * 0.9); // 10% de tolerancia
    
    // Proyectar fecha de completación
    if (currentData.averageMonthlySavings && currentData.averageMonthlySavings > 0) {
      const remainingAmount = profileData.goalAmount - currentData.currentSavings;
      const monthsToComplete = Math.ceil(remainingAmount / currentData.averageMonthlySavings);
      
      const completionDate = new Date();
      completionDate.setMonth(completionDate.getMonth() + monthsToComplete);
      metrics.projectedCompletion = completionDate.toISOString().split('T')[0];
      
      // Determinar si necesita ajuste
      const originalTargetDate = profileData.customGoalDate || 
        new Date(Date.now() + (totalMonths * 30 * 24 * 60 * 60 * 1000)).toISOString().split('T')[0];
      
      metrics.adjustmentNeeded = completionDate > new Date(originalTargetDate);
    }
    
    return metrics;
  }

  /**
   * ========================================
   * MÉTODOS DE FACTURAS/CUENTAS (BILLS)
   * ========================================
   */

  /**
   * Crear nueva factura
   * @param {Object} billData - Datos de la factura a crear
   * @returns {Promise<Object>} - Factura creada
   */
  async createBill(billData) {
    try {
      console.log('🧾 Creando nueva factura:', billData);
      const response = await this.apiClient.post('/agtfin/bills', billData);
      console.log('✅ Factura creada exitosamente:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ Error creando factura:', error);
      throw error;
    }
  }

  /**
   * Obtener todas las facturas del usuario
   * @param {Object} options - Opciones de filtrado
   * @returns {Promise<Object>} - Lista de facturas
   */
  async getBills(options = {}) {
    try {
      console.log('📋 Obteniendo facturas con opciones:', options);
      const params = new URLSearchParams();
      
      if (options.status) params.append('status', options.status);
      if (options.startDate) params.append('startDate', options.startDate);
      if (options.endDate) params.append('endDate', options.endDate);
      if (options.limit) params.append('limit', options.limit.toString());
      
      const queryString = params.toString();
      const url = queryString ? `/agtfin/bills?${queryString}` : '/agtfin/bills';
      
      const response = await this.apiClient.get(url);
      console.log('✅ Facturas obtenidas:', response.data.bills?.length || 0);
      return response.data;
    } catch (error) {
      console.error('❌ Error obteniendo facturas:', error);
      throw error;
    }
  }

  /**
   * Obtener factura por ID
   * @param {string} billId - ID de la factura
   * @returns {Promise<Object>} - Datos de la factura
   */
  async getBillById(billId) {
    try {
      console.log('🔍 Obteniendo factura por ID:', billId);
      const response = await this.apiClient.get(`/agtfin/bills/${billId}`);
      return response.data;
    } catch (error) {
      console.error('❌ Error obteniendo factura por ID:', error);
      throw error;
    }
  }

  /**
   * Actualizar factura
   * @param {string} billId - ID de la factura
   * @param {Object} updateData - Datos a actualizar
   * @returns {Promise<Object>} - Factura actualizada
   */
  async updateBill(billId, updateData) {
    try {
      console.log('🔄 Actualizando factura:', billId, updateData);
      const response = await this.apiClient.put(`/agtfin/bills/${billId}`, updateData);
      console.log('✅ Factura actualizada exitosamente');
      return response.data;
    } catch (error) {
      console.error('❌ Error actualizando factura:', error);
      throw error;
    }
  }

  /**
   * Marcar factura como pagada
   * @param {string} billId - ID de la factura
   * @param {string} paidDate - Fecha de pago (opcional)
   * @returns {Promise<Object>} - Factura actualizada
   */
  async markBillAsPaid(billId, paidDate = null) {
    try {
      console.log('💳 Marcando factura como pagada:', billId);
      const response = await this.apiClient.put(`/agtfin/bills/${billId}/pay`, {
        paidDate: paidDate || new Date().toISOString().split('T')[0]
      });
      console.log('✅ Factura marcada como pagada exitosamente');
      return response.data;
    } catch (error) {
      console.error('❌ Error marcando factura como pagada:', error);
      throw error;
    }
  }

  /**
   * Eliminar factura
   * @param {string} billId - ID de la factura
   * @returns {Promise<Object>} - Confirmación de eliminación
   */
  async deleteBill(billId) {
    try {
      console.log('🗑️ Eliminando factura:', billId);
      const response = await this.apiClient.delete(`/agtfin/bills/${billId}`);
      console.log('✅ Factura eliminada exitosamente');
      return response.data;
    } catch (error) {
      console.error('❌ Error eliminando factura:', error);
      throw error;
    }
  }

  /**
   * Obtener facturas por vencer
   * @param {number} days - Días hacia el futuro para buscar (por defecto 7)
   * @returns {Promise<Object>} - Facturas por vencer
   */
  async getUpcomingBills(days = 7) {
    try {
      console.log('📅 Obteniendo facturas por vencer');
      const response = await this.apiClient.get(`/agtfin/bills/upcoming?days=${days}`);
      return response.data;
    } catch (error) {
      console.error('❌ Error obteniendo facturas por vencer:', error);
      throw error;
    }
  }

  /**
   * Obtener facturas vencidas
   * @returns {Promise<Object>} - Facturas vencidas
   */
  async getOverdueBills() {
    try {
      console.log('⚠️ Obteniendo facturas vencidas');
      const response = await this.apiClient.get('/agtfin/bills/overdue');
      return response.data;
    } catch (error) {
      console.error('❌ Error obteniendo facturas vencidas:', error);
      throw error;
    }
  }

  /**
   * Obtener resumen de facturas
   * @param {string} startDate - Fecha de inicio (opcional)
   * @param {string} endDate - Fecha de fin (opcional)
   * @returns {Promise<Object>} - Resumen de facturas
   */
  async getBillsSummary(startDate, endDate) {
    try {
      console.log('📊 Obteniendo resumen de facturas');
      const params = new URLSearchParams();
      if (startDate) params.append('startDate', startDate);
      if (endDate) params.append('endDate', endDate);
      
      const queryString = params.toString();
      const url = queryString ? `/agtfin/bills/summary?${queryString}` : '/agtfin/bills/summary';
      
      const response = await this.apiClient.get(url);
      return response.data;
    } catch (error) {
      console.error('❌ Error obteniendo resumen de facturas:', error);
      throw error;
    }
  }

  /**
   * Obtener facturas del mes actual
   * @returns {Promise<Object>} - Facturas del mes actual con resumen
   */
  async getCurrentMonthBills() {
    try {
      console.log('📅 Obteniendo facturas del mes actual');
      const response = await this.apiClient.get('/agtfin/bills/current-month');
      return response.data;
    } catch (error) {
      console.error('❌ Error obteniendo facturas del mes actual:', error);
      throw error;
    }
  }

  /**
   * Actualizar estados de facturas vencidas
   * @returns {Promise<Object>} - Facturas actualizadas
   */
  async updateOverdueStatus() {
    try {
      console.log('🔄 Actualizando estados de facturas vencidas');
      const response = await this.apiClient.put('/agtfin/bills/update-overdue');
      return response.data;
    } catch (error) {
      console.error('❌ Error actualizando estados de facturas vencidas:', error);
      throw error;
    }
  }

  /**
   * ========================================
   * MÉTODOS DE PRODUCTOS FINANCIEROS
   * ========================================
   */

  /**
   * Crear nuevo producto financiero
   * @param {Object} productData - Datos del producto financiero a crear
   * @returns {Promise<Object>} - Producto financiero creado
   */
  async createFinancialProduct(productData) {
    try {
      console.log('💳 Creando nuevo producto financiero:', productData);
      const response = await this.apiClient.post('/agtfin/products', productData);
      console.log('✅ Producto financiero creado exitosamente:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ Error creando producto financiero:', error);
      throw error;
    }
  }

  /**
   * Obtener todos los productos financieros del usuario
   * @param {Object} options - Opciones de filtrado
   * @returns {Promise<Object>} - Lista de productos financieros
   */
  async getFinancialProducts(options = {}) {
    try {
      console.log('📋 Obteniendo productos financieros con opciones:', options);
      const params = new URLSearchParams();

      if (options.productType) params.append('productType', options.productType);
      if (options.status) params.append('status', options.status);
      if (options.limit) params.append('limit', options.limit.toString());

      const queryString = params.toString();
      const url = queryString ? `/agtfin/products?${queryString}` : '/agtfin/products';

      const response = await this.apiClient.get(url);
      console.log('✅ Productos financieros obtenidos:', response.data.products?.length || 0);
      return response.data;
    } catch (error) {
      console.error('❌ Error obteniendo productos financieros:', error);
      throw error;
    }
  }

  /**
   * Obtener producto financiero por ID
   * @param {string} productId - ID del producto financiero
   * @returns {Promise<Object>} - Datos del producto financiero
   */
  async getFinancialProductById(productId) {
    try {
      console.log('🔍 Obteniendo producto financiero por ID:', productId);
      const response = await this.apiClient.get(`/agtfin/products/${productId}`);
      return response.data;
    } catch (error) {
      console.error('❌ Error obteniendo producto financiero por ID:', error);
      throw error;
    }
  }

  /**
   * Actualizar producto financiero
   * @param {string} productId - ID del producto financiero
   * @param {Object} updateData - Datos a actualizar
   * @returns {Promise<Object>} - Producto financiero actualizado
   */
  async updateFinancialProduct(productId, updateData) {
    try {
      console.log('🔄 Actualizando producto financiero:', productId, updateData);
      const response = await this.apiClient.put(`/agtfin/products/${productId}`, updateData);
      console.log('✅ Producto financiero actualizado exitosamente');
      return response.data;
    } catch (error) {
      console.error('❌ Error actualizando producto financiero:', error);
      throw error;
    }
  }

  /**
   * Eliminar producto financiero
   * @param {string} productId - ID del producto financiero
   * @returns {Promise<Object>} - Confirmación de eliminación
   */
  async deleteFinancialProduct(productId) {
    try {
      console.log('🗑️ Eliminando producto financiero:', productId);
      const response = await this.apiClient.delete(`/agtfin/products/${productId}`);
      console.log('✅ Producto financiero eliminado exitosamente');
      return response.data;
    } catch (error) {
      console.error('❌ Error eliminando producto financiero:', error);
      throw error;
    }
  }

  /**
   * ========================================
   * MÉTODOS DE AHORROS (SAVINGS)
   * ========================================
   */

  /**
   * Crear nueva meta de ahorro
   * @param {Object} savingData - Datos de la meta de ahorro a crear
   * @returns {Promise<Object>} - Meta de ahorro creada
   */
  async createSaving(savingData) {
    try {
      console.log('💰 Creando nueva meta de ahorro:', savingData);
      const response = await this.apiClient.post('/agtfin/savings', savingData);
      console.log('✅ Meta de ahorro creada exitosamente:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ Error creando meta de ahorro:', error);
      throw error;
    }
  }

  /**
   * Obtener todas las metas de ahorro del usuario
   * @param {Object} options - Opciones de filtrado
   * @returns {Promise<Object>} - Lista de metas de ahorro
   */
  async getSavings(options = {}) {
    try {
      console.log('📋 Obteniendo metas de ahorro con opciones:', options);
      const params = new URLSearchParams();
      
      if (options.goalType) params.append('goalType', options.goalType);
      if (options.startDate) params.append('startDate', options.startDate);
      if (options.endDate) params.append('endDate', options.endDate);
      if (options.limit) params.append('limit', options.limit.toString());
      
      const queryString = params.toString();
      const url = queryString ? `/agtfin/savings?${queryString}` : '/agtfin/savings';
      
      const response = await this.apiClient.get(url);
      console.log('✅ Metas de ahorro obtenidas:', response.data.savings?.length || 0);
      return response.data;
    } catch (error) {
      console.error('❌ Error obteniendo metas de ahorro:', error);
      throw error;
    }
  }

  /**
   * Obtener meta de ahorro por ID
   * @param {string} savingId - ID de la meta de ahorro
   * @returns {Promise<Object>} - Datos de la meta de ahorro
   */
  async getSavingById(savingId) {
    try {
      console.log('🔍 Obteniendo meta de ahorro por ID:', savingId);
      const response = await this.apiClient.get(`/agtfin/savings/${savingId}`);
      return response.data;
    } catch (error) {
      console.error('❌ Error obteniendo meta de ahorro por ID:', error);
      throw error;
    }
  }

  /**
   * Actualizar meta de ahorro
   * @param {string} savingId - ID de la meta de ahorro
   * @param {Object} updateData - Datos a actualizar
   * @returns {Promise<Object>} - Meta de ahorro actualizada
   */
  async updateSaving(savingId, updateData) {
    try {
      console.log('🔄 Actualizando meta de ahorro:', savingId, updateData);
      const response = await this.apiClient.put(`/agtfin/savings/${savingId}`, updateData);
      console.log('✅ Meta de ahorro actualizada exitosamente');
      return response.data;
    } catch (error) {
      console.error('❌ Error actualizando meta de ahorro:', error);
      throw error;
    }
  }

  /**
   * Eliminar meta de ahorro
   * @param {string} savingId - ID de la meta de ahorro
   * @returns {Promise<Object>} - Confirmación de eliminación
   */
  async deleteSaving(savingId) {
    try {
      console.log('🗑️ Eliminando meta de ahorro:', savingId);
      const response = await this.apiClient.delete(`/agtfin/savings/${savingId}`);
      console.log('✅ Meta de ahorro eliminada exitosamente');
      return response.data;
    } catch (error) {
      console.error('❌ Error eliminando meta de ahorro:', error);
      throw error;
    }
  }

  /**
   * Agregar dinero a una meta de ahorro
   * @param {string} savingId - ID de la meta de ahorro
   * @param {Object} transactionData - Datos de la transacción (amount, description)
   * @returns {Promise<Object>} - Meta de ahorro actualizada
   */
  async addToSaving(savingId, transactionData) {
    try {
      console.log('💸 Agregando dinero a meta de ahorro:', savingId, transactionData);
      const response = await this.apiClient.post(`/agtfin/savings/${savingId}/add`, transactionData);
      console.log('✅ Dinero agregado exitosamente a la meta de ahorro');
      return response.data;
    } catch (error) {
      console.error('❌ Error agregando dinero a la meta de ahorro:', error);
      throw error;
    }
  }

  /**
   * Retirar dinero de una meta de ahorro
   * @param {string} savingId - ID de la meta de ahorro
   * @param {Object} transactionData - Datos de la transacción (amount, description)
   * @returns {Promise<Object>} - Meta de ahorro actualizada
   */
  async subtractFromSaving(savingId, transactionData) {
    try {
      console.log('💰 Retirando dinero de meta de ahorro:', savingId, transactionData);
      const response = await this.apiClient.post(`/agtfin/savings/${savingId}/subtract`, transactionData);
      console.log('✅ Dinero retirado exitosamente de la meta de ahorro');
      return response.data;
    } catch (error) {
      console.error('❌ Error retirando dinero de la meta de ahorro:', error);
      throw error;
    }
  }

  /**
   * Obtener transacciones de una meta de ahorro
   * @param {string} savingId - ID de la meta de ahorro
   * @param {Object} options - Opciones de filtrado
   * @returns {Promise<Object>} - Lista de transacciones
   */
  async getSavingTransactions(savingId, options = {}) {
    try {
      console.log('📋 Obteniendo transacciones de meta de ahorro:', savingId);
      const params = new URLSearchParams();
      if (options.limit) params.append('limit', options.limit.toString());
      
      const queryString = params.toString();
      const url = queryString 
        ? `/agtfin/savings/${savingId}/transactions?${queryString}` 
        : `/agtfin/savings/${savingId}/transactions`;
      
      const response = await this.apiClient.get(url);
      return response.data;
    } catch (error) {
      console.error('❌ Error obteniendo transacciones de meta de ahorro:', error);
      throw error;
    }
  }

  /**
   * Obtener resumen de todas las metas de ahorro
   * @returns {Promise<Object>} - Resumen de metas de ahorro
   */
  async getSavingsSummary() {
    try {
      console.log('📊 Obteniendo resumen de metas de ahorro');
      const response = await this.apiClient.get('/agtfin/savings/summary');
      return response.data;
    } catch (error) {
      console.error('❌ Error obteniendo resumen de metas de ahorro:', error);
      throw error;
    }
  }

  /**
   * Obtener metas de ahorro próximas a vencer
   * @param {number} days - Días hacia el futuro para buscar (por defecto 30)
   * @returns {Promise<Object>} - Metas de ahorro próximas a vencer
   */
  async getUpcomingSavings(days = 30) {
    try {
      console.log('📅 Obteniendo metas de ahorro próximas a vencer');
      const response = await this.apiClient.get(`/agtfin/savings/upcoming?days=${days}`);
      return response.data;
    } catch (error) {
      console.error('❌ Error obteniendo metas de ahorro próximas a vencer:', error);
      throw error;
    }
  }

  /**
   * Obtener estadísticas de metas de ahorro por tipo
   * @param {string} period - Período de estadísticas (opcional)
   * @returns {Promise<Object>} - Estadísticas de metas de ahorro
   */
  async getSavingsStats(period = null) {
    try {
      console.log('📈 Obteniendo estadísticas de metas de ahorro');
      const url = period ? `/agtfin/savings/stats?period=${period}` : '/agtfin/savings/stats';
      const response = await this.apiClient.get(url);
      return response.data;
    } catch (error) {
      console.error('❌ Error obteniendo estadísticas de metas de ahorro:', error);
      throw error;
    }
  }

  /**
   * Obtener metas de ahorro del mes actual
   * @returns {Promise<Object>} - Metas de ahorro del mes actual con resumen
   */
  async getCurrentMonthSavings() {
    try {
      console.log('📅 Obteniendo metas de ahorro del mes actual');
      const response = await this.apiClient.get('/agtfin/savings/current-month');
      return response.data;
    } catch (error) {
      console.error('❌ Error obteniendo metas de ahorro del mes actual:', error);
      throw error;
    }
  }

  /**
   * Marcar meta de ahorro como completada
   * @param {string} savingId - ID de la meta de ahorro
   * @returns {Promise<Object>} - Meta de ahorro actualizada
   */
  async markSavingAsCompleted(savingId) {
    try {
      console.log('✅ Marcando meta de ahorro como completada:', savingId);
      const response = await this.apiClient.put(`/agtfin/savings/${savingId}`, {
        status: 'completed',
        completedDate: new Date().toISOString().split('T')[0]
      });
      console.log('✅ Meta de ahorro marcada como completada exitosamente');
      return response.data;
    } catch (error) {
      console.error('❌ Error marcando meta de ahorro como completada:', error);
      throw error;
    }
  }

  /**
   * Pausar/Reanudar meta de ahorro
   * @param {string} savingId - ID de la meta de ahorro
   * @param {boolean} isPaused - true para pausar, false para reanudar
   * @returns {Promise<Object>} - Meta de ahorro actualizada
   */
  async toggleSavingStatus(savingId, isPaused) {
    try {
      const action = isPaused ? 'pausar' : 'reanudar';
      console.log(`⏸️ ${action} meta de ahorro:`, savingId);
      
      const response = await this.apiClient.put(`/agtfin/savings/${savingId}`, {
        status: isPaused ? 'paused' : 'active'
      });
      
      console.log(`✅ Meta de ahorro ${action}da exitosamente`);
      return response.data;
    } catch (error) {
      console.error(`❌ Error al ${isPaused ? 'pausar' : 'reanudar'} meta de ahorro:`, error);
      throw error;
    }
  }

  /**
   * Calcular progreso de ahorro mensual recomendado
   * @param {number} targetAmount - Monto objetivo
   * @param {string} targetDate - Fecha objetivo
   * @param {number} currentAmount - Monto actual ahorrado
   * @returns {Object} - Cálculos de progreso y recomendaciones
   */
  calculateSavingProgress(targetAmount, targetDate, currentAmount = 0) {
    try {
      const today = new Date();
      const target = new Date(targetDate);
      const totalDays = Math.ceil((target - today) / (1000 * 60 * 60 * 24));
      const totalMonths = totalDays / 30.44; // Promedio de días por mes
      
      const remainingAmount = Math.max(0, targetAmount - currentAmount);
      const monthlyRequired = totalMonths > 0 ? remainingAmount / totalMonths : 0;
      const dailyRequired = totalDays > 0 ? remainingAmount / totalDays : 0;
      
      const progressPercentage = targetAmount > 0 ? (currentAmount / targetAmount) * 100 : 0;
      
      // Determinar si está en camino
      const expectedProgress = totalDays > 0 ? Math.max(0, 100 - ((totalDays / 365) * 100)) : 100;
      const isOnTrack = progressPercentage >= (expectedProgress * 0.8); // 20% de tolerancia
      
      return {
        progressPercentage: Math.min(progressPercentage, 100),
        remainingAmount,
        monthlyRequired: Math.max(0, monthlyRequired),
        dailyRequired: Math.max(0, dailyRequired),
        totalDays: Math.max(0, totalDays),
        totalMonths: Math.max(0, totalMonths),
        isOnTrack,
        expectedProgress,
        status: progressPercentage >= 100 ? 'completed' : 
                totalDays <= 0 ? 'overdue' : 
                isOnTrack ? 'on_track' : 'behind'
      };
    } catch (error) {
      console.error('❌ Error calculando progreso de ahorro:', error);
      return {
        progressPercentage: 0,
        remainingAmount: targetAmount,
        monthlyRequired: 0,
        dailyRequired: 0,
        totalDays: 0,
        totalMonths: 0,
        isOnTrack: false,
        expectedProgress: 0,
        status: 'error'
      };
    }
  }

  /**
   * Obtener recomendaciones personalizadas para una meta de ahorro
   * @param {Object} savingData - Datos de la meta de ahorro
   * @param {Object} userProfile - Perfil del usuario
   * @returns {Array} - Array de recomendaciones
   */
  getSavingRecommendations(savingData, userProfile) {
    const recommendations = [];
    const progress = this.calculateSavingProgress(
      savingData.targetAmount,
      savingData.targetDate,
      savingData.currentAmount
    );
    
    // Recomendaciones basadas en progreso
    if (progress.status === 'behind') {
      recommendations.push({
        type: 'warning',
        title: 'Acelera tu ahorro',
        message: `Necesitas ahorrar ${this.formatCurrency(progress.monthlyRequired, userProfile?.currency)} por mes para alcanzar tu meta.`,
        priority: 'high',
        action: 'Considera reducir gastos no esenciales o buscar ingresos adicionales.'
      });
    } else if (progress.status === 'on_track') {
      recommendations.push({
        type: 'success',
        title: '¡Vas por buen camino!',
        message: `Mantén el ritmo ahorrando ${this.formatCurrency(progress.monthlyRequired, userProfile?.currency)} por mes.`,
        priority: 'medium',
        action: 'Automatiza tus ahorros para mantener la consistencia.'
      });
    }
    
    // Recomendaciones basadas en tipo de meta
    if (savingData.goalType === 'emergency') {
      recommendations.push({
        type: 'info',
        title: 'Fondo de emergencia',
        message: 'Tu fondo de emergencia debería cubrir 3-6 meses de gastos esenciales.',
        priority: 'high',
        action: 'Considera abrir una cuenta de ahorros separada con fácil acceso pero que genere rendimientos.'
      });
    } else if (savingData.goalType === 'investment') {
      recommendations.push({
        type: 'info',
        title: 'Inversión',
        message: 'Antes de invertir, asegúrate de tener tu fondo de emergencia completo.',
        priority: 'medium',
        action: 'Investiga diferentes opciones de inversión según tu perfil de riesgo.'
      });
    }
    
    // Recomendaciones basadas en tiempo restante
    if (progress.totalDays < 30) {
      recommendations.push({
        type: 'warning',
        title: 'Meta próxima a vencer',
        message: `Solo quedan ${progress.totalDays} días para alcanzar tu meta.`,
        priority: 'high',
        action: 'Evalúa si es realista alcanzarla o si necesitas ajustar la fecha objetivo.'
      });
    }
    
    // Recomendaciones basadas en monto mensual requerido vs ingresos
    if (userProfile?.monthlyIncome && progress.monthlyRequired > 0) {
      const savingPercentage = (progress.monthlyRequired / userProfile.monthlyIncome) * 100;
      
      if (savingPercentage > 30) {
        recommendations.push({
          type: 'warning',
          title: 'Meta muy ambiciosa',
          message: `Necesitas ahorrar ${Math.round(savingPercentage)}% de tus ingresos mensuales.`,
          priority: 'high',
          action: 'Considera extender el plazo o reducir el monto objetivo para que sea más realista.'
        });
      } else if (savingPercentage < 10) {
        recommendations.push({
          type: 'success',
          title: 'Meta alcanzable',
          message: `Solo necesitas ahorrar ${Math.round(savingPercentage)}% de tus ingresos.`,
          priority: 'low',
          action: 'Podrías considerar aumentar el monto o acortar el plazo para un mayor desafío.'
        });
      }
    }
    
    return recommendations.sort((a, b) => {
      const priorityOrder = { 'high': 3, 'medium': 2, 'low': 1 };
      return priorityOrder[b.priority] - priorityOrder[a.priority];
    });
  }

  /**
   * Validar datos de meta de ahorro
   * @param {Object} savingData - Datos de la meta de ahorro
   * @returns {Object} - Resultado de validación
   */
  validateSavingData(savingData) {
    const errors = [];
    const warnings = [];
    
    // Validaciones obligatorias
    if (!savingData.name || savingData.name.trim().length < 1) {
      errors.push('El nombre de la meta es obligatorio');
    }
    
    if (!savingData.targetAmount || parseFloat(savingData.targetAmount) <= 0) {
      errors.push('El monto objetivo debe ser mayor a 0');
    }
    
    if (!savingData.targetDate) {
      errors.push('La fecha objetivo es obligatoria');
    } else {
      const targetDate = new Date(savingData.targetDate);
      const today = new Date();
      
      if (targetDate <= today) {
        errors.push('La fecha objetivo debe ser futura');
      }
    }
    
    if (!savingData.goalType) {
      errors.push('El tipo de meta es obligatorio');
    }
    
    // Validaciones de coherencia
    if (savingData.currentAmount && parseFloat(savingData.currentAmount) < 0) {
      errors.push('El monto actual no puede ser negativo');
    }
    
    if (savingData.currentAmount && savingData.targetAmount) {
      const current = parseFloat(savingData.currentAmount);
      const target = parseFloat(savingData.targetAmount);
      
      if (current > target) {
        warnings.push('El monto actual es mayor al objetivo. La meta se marcará como completada.');
      }
    }
    
    // Validaciones de monto
    if (savingData.targetAmount) {
      const amount = parseFloat(savingData.targetAmount);
      
      if (amount > 10000000) {
        warnings.push('El monto objetivo es muy alto. Asegúrate de que sea realista.');
      } else if (amount < 100) {
        warnings.push('El monto objetivo es muy bajo. Considera aumentarlo para un mayor impacto.');
      }
    }
    
    return {
      isValid: errors.length === 0,
      errors,
      warnings
    };
  }

  /**
   * Generar plantillas de metas de ahorro sugeridas
   * @param {Object} userProfile - Perfil del usuario
   * @returns {Array} - Array de plantillas sugeridas
   */
  getSavingTemplates(userProfile) {
    const templates = [];
    const monthlyIncome = userProfile?.monthlyIncome || 0;
    const currency = userProfile?.currency || 'USD';
    
    // Plantilla de fondo de emergencia
    templates.push({
      id: 'emergency_fund',
      name: 'Fondo de Emergencia',
      targetAmount: monthlyIncome * 6, // 6 meses de ingresos
      goalType: 'emergency',
      priority: 'high',
      description: 'Fondo para cubrir 6 meses de gastos en caso de emergencia',
      recommendedTimeframe: '12_months',
      tips: [
        'Debería cubrir 3-6 meses de gastos esenciales',
        'Manténlo en una cuenta separada de fácil acceso',
        'Es tu primera prioridad financiera'
      ]
    });
    
    // Plantilla de vacaciones
    templates.push({
      id: 'vacation_fund',
      name: 'Vacaciones de Ensueño',
      targetAmount: monthlyIncome * 2, // 2 meses de ingresos
      goalType: 'vacation',
      priority: 'medium',
      description: 'Ahorro para unas vacaciones especiales',
      recommendedTimeframe: '12_months',
      tips: [
        'Planifica con anticipación para mejores precios',
        'Investiga destinos y costos promedio',
        'Considera viajar en temporada baja'
      ]
    });
    
    // Plantilla de inversión
    if (monthlyIncome > 0) {
      templates.push({
        id: 'investment_fund',
        name: 'Capital de Inversión',
        targetAmount: monthlyIncome * 3, // 3 meses de ingresos
        goalType: 'investment',
        priority: 'medium',
        description: 'Capital inicial para comenzar a invertir',
        recommendedTimeframe: '18_months',
        tips: [
          'Solo invierte después de tener tu fondo de emergencia',
          'Edúcate sobre diferentes opciones de inversión',
          'Diversifica tu portafolio para reducir riesgos'
        ]
      });
    }
    
    // Plantilla de compra importante
    templates.push({
      id: 'major_purchase',
      name: 'Compra Importante',
      targetAmount: monthlyIncome * 4, // 4 meses de ingresos
      goalType: 'purchase',
      priority: 'low',
      description: 'Ahorro para una compra importante (auto, electrodomésticos, etc.)',
      recommendedTimeframe: '24_months',
      tips: [
        'Investiga precios y opciones antes de comprar',
        'Considera si realmente necesitas la compra',
        'Busca ofertas y descuentos especiales'
      ]
    });
    
    // Ajustar montos según la moneda
    if (currency !== 'USD') {
      const exchangeRates = {
        'EUR': 0.85,
        'CLP': 900,
        'MXN': 18,
        'ARS': 800,
        'COP': 4000,
        'PEN': 3.7,
        'UYU': 43
      };
      
      const rate = exchangeRates[currency] || 1;
      templates.forEach(template => {
        if (currency === 'USD') return;
        
        if (rate > 1) {
          // Para monedas con valores altos (CLP, ARS, etc.)
          template.targetAmount = Math.round(template.targetAmount * rate);
        } else {
          // Para monedas con valores bajos (EUR)
          template.targetAmount = Math.round(template.targetAmount * rate);
        }
      });
    }
    
    return templates;
  }

  /**
   * Test de conectividad - Método para verificar que el servicio funciona
   */
  async testConnection() {
    try {
      console.log('🔍 Probando conexión con el API...');
      const response = await this.apiClient.get('/health');
      console.log('✅ Conexión exitosa:', response.data);
      return true;
    } catch (error) {
      console.error('❌ Error de conexión:', error.message);
      return false;
    }
  }

  /**
   * ========================================
   * MÉTODOS DE DASHBOARD
   * ========================================
   */

  /**
   * Obtener alertas y notificaciones del dashboard
   * @returns {Promise<Object>} - Alertas y notificaciones
   */
  async getDashboardAlerts() {
    try {
      console.log('🚨 Obteniendo alertas del dashboard');
      const response = await this.apiClient.get('/agtfin/dashboard/alerts');
      console.log('✅ Alertas obtenidas exitosamente:', response.data.count || 0);
      return response.data;
    } catch (error) {
      console.error('❌ Error obteniendo alertas del dashboard:', error);
      throw error;
    }
  }

  /**
   * ========================================
   * MÉTODO DE PRUEBA DE DASHBOARD
   * ========================================
   */

  /**
   * Probar la conectividad del dashboard
   */
  async testDashboardConnection() {
    try {
      console.log('🔍 Probando conectividad del dashboard...');
      
      const tests = [];
      
      // Probar estadísticas financieras
      try {
        await this.getFinancialStats();
        tests.push({ test: 'Financial Stats', status: '✅ OK' });
      } catch (error) {
        tests.push({ test: 'Financial Stats', status: '❌ ERROR', error: error.message });
      }
      
      // Probar alertas
      try {
        await this.getDashboardAlerts();
        tests.push({ test: 'Dashboard Alerts', status: '✅ OK' });
      } catch (error) {
        tests.push({ test: 'Dashboard Alerts', status: '❌ ERROR', error: error.message });
      }
      
      // Probar datos de gráficos
      try {
        await this.getChartData('budget-overview');
        tests.push({ test: 'Chart Data', status: '✅ OK' });
      } catch (error) {
        tests.push({ test: 'Chart Data', status: '❌ ERROR', error: error.message });
      }
      
      console.log('📊 Resultados de prueba del dashboard:', tests);
      
      const allPassed = tests.every(test => test.status === '✅ OK');
      return {
        success: allPassed,
        tests,
        message: allPassed ? 'Todas las pruebas del dashboard pasaron' : 'Algunas pruebas fallaron'
      };
    } catch (error) {
      console.error('❌ Error en prueba del dashboard:', error);
      return {
        success: false,
        error: error.message,
        message: 'Error general en la prueba del dashboard'
      };
    }
  }

  /**
   * Obtener estadísticas financieras para el dashboard principal con filtros de fecha
   * @param {Object} options - Opciones de filtrado
   * @param {string} options.startDate - Fecha de inicio (YYYY-MM-DD)
   * @param {string} options.endDate - Fecha de fin (YYYY-MM-DD)
   * @param {number} options.month - Mes específico (0-11)
   * @param {number} options.year - Año específico
   * @returns {Promise<Object>} - Estadísticas financieras completas
   */
  async getFinancialStats(options = {}) {
    try {
      console.log('📊 Obteniendo estadísticas financieras para dashboard con opciones:', options);
      
      const params = new URLSearchParams();
      
      // Si se proporciona mes y año, calcular startDate y endDate
      if (options.month !== undefined && options.year !== undefined) {
        const startDate = new Date(options.year, options.month, 1).toISOString().split('T')[0];
        const endDate = new Date(options.year, options.month + 1, 0).toISOString().split('T')[0];
        params.append('startDate', startDate);
        params.append('endDate', endDate);
        console.log(`📅 Calculado rango para ${options.month + 1}/${options.year}: ${startDate} - ${endDate}`);
      } else {
        // Usar fechas específicas si se proporcionan
        if (options.startDate) params.append('startDate', options.startDate);
        if (options.endDate) params.append('endDate', options.endDate);
      }
      
      const queryString = params.toString();
      const url = queryString ? `/agtfin/dashboard/financial-stats?${queryString}` : '/agtfin/dashboard/financial-stats';
      
      const response = await this.apiClient.get(url);
      console.log('✅ Estadísticas financieras obtenidas exitosamente');
      return response.data;
    } catch (error) {
      console.error('❌ Error obteniendo estadísticas financieras:', error);
      throw error;
    }
  }

  /**
   * Obtener resumen general del dashboard con filtros de fecha
   * @param {Object} options - Opciones de filtrado
   * @param {string} options.period - Período para el resumen ('current-month', '3months', '6months', 'year')
   * @param {string} options.startDate - Fecha de inicio específica
   * @param {string} options.endDate - Fecha de fin específica
   * @param {number} options.month - Mes específico (0-11)
   * @param {number} options.year - Año específico
   * @returns {Promise<Object>} - Resumen del dashboard
   */
  async getDashboardSummary(options = {}) {
    try {
      const { period = 'current-month' } = options;
      console.log('📋 Obteniendo resumen del dashboard con opciones:', options);
      
      const params = new URLSearchParams();
      
      // Si se proporciona mes y año, calcular startDate y endDate
      if (options.month !== undefined && options.year !== undefined) {
        const startDate = new Date(options.year, options.month, 1).toISOString().split('T')[0];
        const endDate = new Date(options.year, options.month + 1, 0).toISOString().split('T')[0];
        params.append('startDate', startDate);
        params.append('endDate', endDate);
        params.append('period', 'custom');
      } else {
        // Usar período o fechas específicas
        if (period) params.append('period', period);
        if (options.startDate) params.append('startDate', options.startDate);
        if (options.endDate) params.append('endDate', options.endDate);
      }
      
      const queryString = params.toString();
      const url = queryString ? `/agtfin/dashboard/summary?${queryString}` : '/agtfin/dashboard/summary';
      
      const response = await this.apiClient.get(url);
      console.log('✅ Resumen del dashboard obtenido exitosamente');
      return response.data;
    } catch (error) {
      console.error('❌ Error obteniendo resumen del dashboard:', error);
      throw error;
    }
  }

  /**
   * Obtener datos para gráficos del dashboard con filtros de fecha
   * @param {string} type - Tipo de gráfico ('income-trend', 'expense-categories', 'budget-overview', 'savings-progress')
   * @param {Object} options - Opciones de filtrado
   * @param {string} options.period - Período para los datos ('3months', '6months', 'year')
   * @param {string} options.startDate - Fecha de inicio específica
   * @param {string} options.endDate - Fecha de fin específica
   * @param {number} options.month - Mes específico (0-11)
   * @param {number} options.year - Año específico
   * @returns {Promise<Object>} - Datos del gráfico
   */
  async getChartData(type, options = {}) {
    try {
      const { period = '6months' } = options;
      console.log('📈 Obteniendo datos del gráfico:', type, 'con opciones:', options);
      
      const params = new URLSearchParams();
      params.append('type', type);
      
      // Si se proporciona mes y año, calcular startDate y endDate
      if (options.month !== undefined && options.year !== undefined) {
        const startDate = new Date(options.year, options.month, 1).toISOString().split('T')[0];
        const endDate = new Date(options.year, options.month + 1, 0).toISOString().split('T')[0];
        params.append('startDate', startDate);
        params.append('endDate', endDate);
        params.append('period', 'custom');
      } else {
        // Usar período o fechas específicas
        params.append('period', period);
        if (options.startDate) params.append('startDate', options.startDate);
        if (options.endDate) params.append('endDate', options.endDate);
      }
      
      const response = await this.apiClient.get(`/agtfin/dashboard/charts?${params.toString()}`);
      console.log('✅ Datos del gráfico obtenidos exitosamente');
      return response.data;
    } catch (error) {
      console.error('❌ Error obteniendo datos del gráfico:', error);
      throw error;
    }
  }

  /**
   * Obtener reporte financiero completo con filtros de fecha
   * @param {Object} options - Opciones de filtrado
   * @param {string} options.period - Período para el reporte ('current-month', '3months', '6months', 'year')
   * @param {string} options.startDate - Fecha de inicio específica
   * @param {string} options.endDate - Fecha de fin específica
   * @param {number} options.month - Mes específico (0-11)
   * @param {number} options.year - Año específico
   * @returns {Promise<Object>} - Reporte financiero completo
   */
  async getFinancialReport(options = {}) {
    try {
      const { period = 'current-month' } = options;
      console.log('📄 Obteniendo reporte financiero con opciones:', options);
      
      const params = new URLSearchParams();
      
      // Si se proporciona mes y año, calcular startDate y endDate
      if (options.month !== undefined && options.year !== undefined) {
        const startDate = new Date(options.year, options.month, 1).toISOString().split('T')[0];
        const endDate = new Date(options.year, options.month + 1, 0).toISOString().split('T')[0];
        params.append('startDate', startDate);
        params.append('endDate', endDate);
        params.append('period', 'custom');
      } else {
        // Usar período o fechas específicas
        if (period) params.append('period', period);
        if (options.startDate) params.append('startDate', options.startDate);
        if (options.endDate) params.append('endDate', options.endDate);
      }
      
      const queryString = params.toString();
      const url = queryString ? `/agtfin/dashboard/report?${queryString}` : '/agtfin/dashboard/report';
      
      const response = await this.apiClient.get(url);
      console.log('✅ Reporte financiero obtenido exitosamente');
      return response.data;
    } catch (error) {
      console.error('❌ Error obteniendo reporte financiero:', error);
      throw error;
    }
  }

  /**
   * Obtener datos específicos para el componente FinDashboard con filtros de fecha
   * @param {Object} options - Opciones de filtrado
   * @param {string} options.startDate - Fecha de inicio (YYYY-MM-DD)
   * @param {string} options.endDate - Fecha de fin (YYYY-MM-DD)
   * @param {number} options.month - Mes específico (0-11, donde 0 = Enero)
   * @param {number} options.year - Año específico (ej: 2024)
   * @returns {Promise<Object>} - Datos formateados para el dashboard
   */
  async getFinDashboardData(options = {}) {
    try {
      console.log('🎯 Obteniendo datos específicos para FinDashboard con opciones:', options);
      
      // Obtener estadísticas financieras principales con filtros
      const statsResponse = await this.getFinancialStats(options);
      
      // Obtener alertas para mostrar notificaciones
      const alertsResponse = await this.getDashboardAlerts();
      
      // Formatear datos para el componente FinDashboard
      const dashboardData = {
        financialStats: statsResponse.financialStats,
        userProfile: statsResponse.userProfile,
        alerts: alertsResponse.alerts || [],
        alertsCount: alertsResponse.count || 0,
        period: statsResponse.period,
        selectedPeriod: options, // Incluir las opciones de filtrado utilizadas
        lastUpdated: new Date().toISOString()
      };
      
      console.log('✅ Datos del FinDashboard obtenidos exitosamente para período:', options);
      return dashboardData;
    } catch (error) {
      console.error('❌ Error obteniendo datos del FinDashboard:', error);
      throw error;
    }
  }

  /**
   * Obtener datos para un tipo específico de gráfico con formato optimizado y filtros de fecha
   * @param {string} chartType - Tipo de gráfico
   * @param {Object} options - Opciones adicionales
   * @param {string} options.period - Período ('6months', '3months', etc.)
   * @param {string} options.currency - Moneda ('USD', 'EUR', etc.)
   * @param {string} options.startDate - Fecha de inicio específica
   * @param {string} options.endDate - Fecha de fin específica
   * @param {number} options.month - Mes específico (0-11)
   * @param {number} options.year - Año específico
   * @returns {Promise<Object>} - Datos del gráfico formateados
   */
  async getFormattedChartData(chartType, options = {}) {
    try {
      const { currency = 'USD' } = options;
      // const period = options.period || '6months'; // Unused parameter

      console.log('📊 Obteniendo datos formateados para gráfico:', chartType, 'con opciones:', options);

      const response = await this.getChartData(chartType, options);
      const chartData = response.chartData;
      
      // Formatear datos según el tipo de gráfico
      let formattedData = chartData;
      
      switch (chartType) {
        case 'income-trend':
          formattedData = chartData.map(item => ({
            ...item,
            totalFormatted: this.formatCurrency(item.total, currency),
            paycheckFormatted: this.formatCurrency(item.paycheck, currency),
            extraFormatted: this.formatCurrency(item.extra, currency),
            otherFormatted: this.formatCurrency(item.other, currency)
          }));
          break;

        case 'expense-categories':
          formattedData = chartData.map(item => ({
            ...item,
            totalFormatted: this.formatCurrency(item.total, currency),
            percentage: item.total > 0 ? Math.round((item.total / chartData.reduce((sum, cat) => sum + cat.total, 0)) * 100) : 0
          }));
          break;

        case 'budget-overview':
          formattedData = {
            ...chartData,
            incomeFormatted: this.formatCurrency(chartData.income, currency),
            billsFormatted: this.formatCurrency(chartData.bills, currency),
            expensesFormatted: this.formatCurrency(chartData.expenses, currency),
            remainingFormatted: this.formatCurrency(chartData.remaining, currency)
          };
          break;

        case 'savings-progress':
          formattedData = chartData.map(item => ({
            ...item,
            currentAmountFormatted: this.formatCurrency(item.currentAmount, currency),
            targetAmountFormatted: this.formatCurrency(item.targetAmount, currency),
            remainingAmount: item.targetAmount - item.currentAmount,
            remainingAmountFormatted: this.formatCurrency(item.targetAmount - item.currentAmount, currency)
          }));
          break;
        default:
          console.warn(`Tipo de gráfico desconocido: ${chartType}, retornando datos sin formato`);
          break;
      }
      
      return {
        data: formattedData,
        type: chartType,
        period: response.period,
        currency,
        selectedOptions: options // Incluir las opciones utilizadas
      };
    } catch (error) {
      console.error('❌ Error obteniendo datos formateados del gráfico:', error);
      throw error;
    }
  }

  /**
   * Obtener datos de tendencias para múltiples métricas con filtros de fecha
   * @param {Array} metrics - Métricas a obtener ('income', 'expenses', 'savings')
   * @param {Object} options - Opciones de filtrado
   * @param {string} options.period - Período para las tendencias
   * @param {string} options.startDate - Fecha de inicio específica
   * @param {string} options.endDate - Fecha de fin específica
   * @param {number} options.month - Mes específico (0-11)
   * @param {number} options.year - Año específico
   * @returns {Promise<Object>} - Datos de tendencias
   */
  async getTrendData(metrics = ['income', 'expenses', 'savings'], options = {}) {
    try {
      // const period = options.period || '6months'; // Unused parameter
      console.log('📈 Obteniendo datos de tendencias para:', metrics, 'con opciones:', options);
      
      const trendData = {};
      
      // Obtener datos de ingresos si se solicita
      if (metrics.includes('income')) {
        const incomeData = await this.getChartData('income-trend', options);
        trendData.income = incomeData.chartData;
      }
      
      // Obtener datos de gastos si se solicita
      if (metrics.includes('expenses')) {
        const expenseData = await this.getChartData('expense-categories', options);
        trendData.expenses = expenseData.chartData;
      }
      
      // Obtener datos de ahorros si se solicita
      if (metrics.includes('savings')) {
        const savingsData = await this.getChartData('savings-progress', options);
        trendData.savings = savingsData.chartData;
      }
      
      console.log('✅ Datos de tendencias obtenidos exitosamente');
      return {
        ...trendData,
        period: options,
        lastUpdated: new Date().toISOString()
      };
    } catch (error) {
      console.error('❌ Error obteniendo datos de tendencias:', error);
      throw error;
    }
  }

  /**
   * Obtener métricas clave del dashboard en formato compacto con filtros de fecha
   * @param {Object} options - Opciones de filtrado
   * @param {string} options.startDate - Fecha de inicio específica
   * @param {string} options.endDate - Fecha de fin específica
   * @param {number} options.month - Mes específico (0-11)
   * @param {number} options.year - Año específico
   * @returns {Promise<Object>} - Métricas clave
   */
  async getDashboardMetrics(options = {}) {
    try {
      console.log('🎯 Obteniendo métricas clave del dashboard con opciones:', options);
      
      const data = await this.getFinancialStats(options);
      const stats = data.financialStats;
      
      // Extraer métricas clave
      const metrics = {
        monthlyIncome: {
          current: stats.monthlyIncome.current,
          change: stats.monthlyIncome.change,
          trend: stats.monthlyIncome.changeType
        },
        monthlyExpenses: {
          current: stats.expenses.current,
          change: stats.expenses.change,
          trend: stats.expenses.changeType
        },
        savings: {
          current: stats.savings.current,
          progress: stats.savings.progress,
          change: stats.savings.change
        },
        netIncome: stats.summary.netIncome,
        savingsRate: stats.summary.savingsRate,
        budgetUtilization: stats.summary.budgetUtilization || 0,
        currency: data.userProfile.currency,
        selectedPeriod: options, // Incluir período seleccionado
        periodInfo: data.period
      };
      
      console.log('✅ Métricas clave obtenidas exitosamente');
      return metrics;
    } catch (error) {
      console.error('❌ Error obteniendo métricas del dashboard:', error);
      throw error;
    }
  }

  /**
   * NUEVO: Obtener lista de meses disponibles para navegación
   * @param {number} monthsBack - Número de meses hacia atrás para incluir (por defecto 12)
   * @returns {Array} - Array de objetos con información de meses disponibles
   */
  getAvailableMonths(monthsBack = 12) {
    try {
      const months = [];
      const currentDate = new Date();
      
      for (let i = 0; i < monthsBack; i++) {
        const date = new Date(currentDate.getFullYear(), currentDate.getMonth() - i, 1);
        months.push({
          month: date.getMonth(),
          year: date.getFullYear(),
          monthName: MONTHS_ES[date.getMonth()],
          fullName: `${MONTHS_ES[date.getMonth()]} ${date.getFullYear()}`,
          isCurrent: i === 0,
          isValid: true,
          startDate: new Date(date.getFullYear(), date.getMonth(), 1).toISOString().split('T')[0],
          endDate: new Date(date.getFullYear(), date.getMonth() + 1, 0).toISOString().split('T')[0]
        });
      }
      
      return months;
    } catch (error) {
      console.error('❌ Error obteniendo meses disponibles:', error);
      return [];
    }
  }

  /**
   * NUEVO: Validar si un mes/año es válido para consulta
   * @param {number} month - Mes (0-11)
   * @param {number} year - Año
   * @returns {Object} - Resultado de validación
   */
  validateMonthYear(month, year) {
    try {
      const currentDate = new Date();
      const selectedDate = new Date(year, month, 1);
      const minDate = new Date(currentDate.getFullYear(), currentDate.getMonth() - 12, 1);
      
      const isValid = selectedDate >= minDate && selectedDate <= currentDate;
      const isCurrent = month === currentDate.getMonth() && year === currentDate.getFullYear();
      const isFuture = selectedDate > currentDate;
      const isTooOld = selectedDate < minDate;
      
      return {
        isValid,
        isCurrent,
        isFuture,
        isTooOld,
        message: !isValid ? 
          (isFuture ? 'No se pueden consultar meses futuros' : 
           isTooOld ? 'Período muy antiguo, máximo 12 meses atrás' : 
           'Período inválido') : 
          'Período válido'
      };
    } catch (error) {
      console.error('❌ Error validando mes/año:', error);
      return {
        isValid: false,
        message: 'Error en la validación'
      };
    }
  }

  /**
   * NUEVO: Obtener información del período seleccionado
   * @param {number} month - Mes (0-11)
   * @param {number} year - Año
   * @returns {Object} - Información del período
   */
  getPeriodInfo(month, year) {
    try {
      const MONTHS_ES = [
        'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
        'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
      ];
      
      const currentDate = new Date();
      // const selectedDate = new Date(year, month, 1); // Unused variable
      const isCurrent = month === currentDate.getMonth() && year === currentDate.getFullYear();
      
      let timeDescription = '';
      if (isCurrent) {
        timeDescription = 'Mes actual';
      } else {
        const monthsDiff = (currentDate.getFullYear() - year) * 12 + (currentDate.getMonth() - month);
        if (monthsDiff > 0) {
          timeDescription = `${monthsDiff} ${monthsDiff === 1 ? 'mes' : 'meses'} atrás`;
        } else if (monthsDiff < 0) {
          timeDescription = `${Math.abs(monthsDiff)} ${Math.abs(monthsDiff) === 1 ? 'mes' : 'meses'} adelante`;
        }
      }
      
      return {
        month,
        year,
        monthName: MONTHS_ES[month],
        fullName: `${MONTHS_ES[month]} ${year}`,
        shortName: `${MONTHS_ES[month].substring(0, 3)} ${year}`,
        isCurrent,
        timeDescription,
        startDate: new Date(year, month, 1).toISOString().split('T')[0],
        endDate: new Date(year, month + 1, 0).toISOString().split('T')[0],
        daysInMonth: new Date(year, month + 1, 0).getDate()
      };
    } catch (error) {
      console.error('❌ Error obteniendo información del período:', error);
      return null;
    }
  }
}

// Exportamos una instancia singleton del servicio
const agtFinService = new AGTFinService();

// Verificar que el servicio se inicializa correctamente
console.log('🚀 AGTFinService inicializado:', {
  hasApiClient: !!agtFinService.apiClient,
  baseURL: agtFinService.apiClient?.defaults?.baseURL
});

export default agtFinService;
